import React, { ReactNode } from "react";
import { format } from "date-fns";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { MaterialInfo } from "../../../types/domain";
import { SubdirectoryArrow } from "../../Icons";

import styles from "../RecordsSidebar.module.scss";

interface Props {
  baseKey: string;
  title: string;
  isBold?: boolean;
  isBorder?: boolean;
  isWithArrow?: boolean;
  isWithReverseArrow?: boolean;
  dateString?: string | null;
  materials?: MaterialInfo[];
  participantBusinessRoleCode?: string;
  participantDisplayName?: string;
  participantRpraNumber?: string;
  children?: ReactNode;
}

const Row = ({
  baseKey,
  title,
  dateString,
  isBold = false,
  isBorder = false,
  isWithArrow = false,
  isWithReverseArrow = false,
  materials,
  participantBusinessRoleCode,
  participantDisplayName,
  participantRpraNumber,
  children,
}: Props) => {
  const { t } = useTranslation();
  const date = new Date(dateString || "");
  const showArrow = isWithArrow || isWithReverseArrow;
  const participantLabel = participantBusinessRoleCode
    ? t(`businessRoles.${participantBusinessRoleCode}`)
    : "";
  const participantParts = [
    participantLabel,
    participantDisplayName,
    participantRpraNumber,
  ].filter(Boolean);
  const hasParticipant = participantParts.length > 0;

  return (
    <div
      className={classnames(styles.transactionRecordsListItem, {
        [styles.withBorder]: isBorder,
      })}
    >
      <div className={styles.transactionRecordsListItemMain}>
        <div className={styles.row}>
          {showArrow && (
            <SubdirectoryArrow
              className={styles.linkedIcon}
              width={12}
              height={12}
              isUp={isWithReverseArrow}
            />
          )}
          <div className={styles.col}>
            <div className={classnames({ [styles.bold]: isBold })}>{title}</div>
            {hasParticipant && (
              <div className={styles.participant}>
                {participantParts.join(" · ")}
              </div>
            )}
            {children}
          </div>
        </div>
        <div className={styles.transactionRecordsListItemRight}>
          {dateString && (
            <div className={styles.dateItem}>
              <div className={styles.dateLine}>{format(date, "MMM d, yy")}</div>
              <div className={styles.timeline}>{format(date, "h:mm a")}</div>
            </div>
          )}
        </div>
      </div>
      {!!materials && (
        <div className={styles.transactionRecordsListItemSecondary}>
          {materials.map((material: MaterialInfo) => {
            return (
              <div
                key={`${baseKey}-${material.materialCode}`}
                className={styles.transactionRecordsListItemSecondaryItem}
              >
                <div>{material.materialName || material.materialCode}</div>
                <div>
                  {`${Math.round(
                    material.units.unitQuantity
                  ).toLocaleString()} ${material.units.unit}`}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Row;
